import { operators as o, common as c } from '@pendo/aggregations';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import generatePathFilters, { PathFilter } from './aggregation/filter-builder';
import { regionEvalExpression } from '@pendo/middleware';
// Removed semantic search clustering support

type ExcludedEvents = (c.PageId | c.FeatureId | c.TrackTypeId | c.GuideId | c.AgentId)[];

interface ExcludedEvent {
    id: string;
    kind: 'Page' | 'Feature' | 'TrackType' | 'Guide' | 'AIAgent';
}
interface ExcludedEventPathConfig {
    excludeItems: boolean;
    items: ExcludedEvents;
}

const excludedEventsToPathConfig = (
    excludedEvents: ExcludedEvent[] | undefined,
): ExcludedEventPathConfig | Record<never, never> => {
    if (!excludedEvents || excludedEvents.length < 1) {
        return {};
    }

    const hasExcludedItems = excludedEvents.length > 0;
    const mappedExcludedEvents = excludedEvents.map((event) => {
        switch (event.kind) {
            case 'Page':
                return { pageId: event.id };
            case 'Feature':
                return { featureId: event.id };
            case 'TrackType':
                return { trackTypeId: event.id };
            case 'Guide':
                return { guideId: event.id };
            case 'AIAgent':
                return { agentId: event.id };
            default:
                return {};
        }
    });

    return {
        excludeItems: hasExcludedItems,
        items: mappedExcludedEvents,
    };
};

interface BuildConfig extends c.SegmentAggInput {
    pageId?: string;
    featureId?: string;
    trackTypeId?: string;
    guideId?: string;
    guideStepId?: string;
    guideEventTypes?: string;
    guideEvents?: boolean;
    agentId?: string;
    collapseDups: boolean;
    maxInterval: number;
    maxLength: number;
    predecessors: boolean;
    omitPages: boolean;
    features: boolean;
    trackEvents: boolean;
    agentEvents?: boolean;
    appId: c.AppIdType;
    expandedAppIds: Array<string | number>;
    acrossApps: boolean;
    uniqueVisitors: boolean;
    excludedEvents?: ExcludedEvent[];
    selectedAccount?: c.AccountFilterAggInput;
    reportFilters?: PathFilter[];
    eventFilters?: PathFilter[];
    replayData?: boolean;
}

export default {
    name: 'path',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.PATHS,
    requestId: ({ report }: { report: { id: string } }) => `path-${report.id}`,
    response: ({
        forceRun,
        impersonating,
        report,
    }: {
        forceRun: boolean;
        impersonating: boolean;
        report: { id: string };
    }) => {
        return {
            name: report.id,
            location: 'report',
            mimeType: 'application/json',
            report,
            forceRun,
            impersonating,
        };
    },
    build: ({
        timeSeries,
        config: {
            appId,
            acrossApps,
            expandedAppIds = [],
            blacklist,
            predecessors,
            segmentId,
            includeAnonymous,
            omitPages,
            features,
            trackEvents,
            guideEvents,
            agentEvents,
            uniqueVisitors,
            collapseDups,
            pageId,
            featureId,
            trackTypeId,
            guideId,
            guideStepId,
            guideEventTypes,
            agentId,
            maxInterval,
            maxLength,
            excludedEvents,
            selectedAccount,
            reportFilters,
            eventFilters,
            replayData,
        },
    }: {
        timeSeries: c.TimeSeries;
        config: BuildConfig;
    }) => {
        // Semantic search clustering disabled
        const similarityScore = undefined;

        const hasPage = pageId || omitPages === false;
        const hasFeature = featureId || features === true;
        const hasTrackEvent = trackTypeId || trackEvents === true;
        const hasAgentEvent = agentId || agentEvents === true;

        const eventClass = [];
        if (hasPage || hasFeature) {
            eventClass.push('ui');
        }
        if (hasTrackEvent) {
            eventClass.push('track');
        }
        if (hasAgentEvent) {
            eventClass.push('agentic');
        }

        const {
            filterString: anchorFilter,
            hasDevicePropFilter: hasDevicePropEventFilter,
            bulkExpand: bulkExpandEvents,
            hasRegionFilter: hasRegionEventFilter,
        } = generatePathFilters(eventFilters, similarityScore);

        const {
            filterString: reportFilter,
            hasDevicePropFilter: hasDevicePropReportFilter,
            bulkExpand: bulkExpandReport,
            hasRegionFilter: hasRegionReportFilter,
        } = generatePathFilters(reportFilters);

        const bulkExpandTotal = [...new Set([...bulkExpandEvents, ...bulkExpandReport])];
        const bulkExpandOperators = bulkExpandTotal.map((type) => o.bulkExpand(type));
        const hasDevicePropFilter = hasDevicePropEventFilter || hasDevicePropReportFilter;
        const hasRegionFilter = hasRegionEventFilter || hasRegionReportFilter;

        const sourceAppId = expandedAppIds && expandedAppIds.length ? expandedAppIds : appId;

        const source = [
            o.sources.singleEvents({
                timeSeries: { ...timeSeries },
                appId: sourceAppId,
                reverseTime: predecessors,
                eventClass,
            }),
        ];

        return o.build(
            o.settings(blacklist),
            o.pipeline(
                ...source,
                o.identified(includeAnonymous),
                o.segment(segmentId),
                o.accountFilter(selectedAccount),
                hasDevicePropFilter && o.userAgent('parsedUserAgent'),
                ...bulkExpandOperators,
                hasRegionFilter && o.evalExpression({ country_region: regionEvalExpression }),
                !!reportFilter && o.filter(reportFilter),
                o.group(
                    ['visitorId'],
                    o.groupField('path', {
                        path: {
                            pageId,
                            featureId,
                            trackTypeId,
                            guideId,
                            guideStepId,
                            guideEventType: guideEventTypes,
                            agentId,
                            collapseDups,
                            maxInterval,
                            maxLength,
                            predecessors,
                            followAcrossSessions: !!acrossApps,
                            omitPages,
                            features,
                            trackEvents,
                            guideEvents,
                            agentEvents,
                            replayData,
                            ...excludedEventsToPathConfig(excludedEvents),
                            ...(anchorFilter ? { anchorFilter } : {}),
                        },
                    }),
                ),
                o.unwind('path'),
                o.fork(
                    o.pipeline(o.limit(10000)),
                    o.pipeline(
                        o.evalExpression({
                            items: 'map(x, path, x.items)',
                        }),
                        o.reduce({
                            tree: o.treeify({
                                threshold: 0.0001,
                                keySort: true,
                                uniqueVisitors,
                            }),
                        }),
                    ),
                ),
            ),
        );
    },
};
